import type { MailboxInfo } from 'owa-client-types';
import type { MailFolderScenarioType } from 'owa-mail-list-store';
import type { ConversationType, ItemRow } from 'owa-graph-schema';
import getStore from '../store/store';

export function filterItemRowsForCatchUp(
    rows: ItemRow[],
    scenarioType: MailFolderScenarioType,
    mailboxInfo: MailboxInfo
): ItemRow[] {
    const {
        catchUpIncludeAllEmail,
        timeFrame,
        shouldUsePrompt,
        needsReplyItemIds,
        needsActionItemIds,
        needsScheduleItemIds,
    } = getStore(mailboxInfo);

    return rows.filter(row => {
        let scenarioTypeCondition = false;
        switch (scenarioType) {
            case 'catchUpHighPriorityFilter':
                scenarioTypeCondition = (row.CopilotInboxScore ?? 0) >= 4;
                break;
            case 'catchUpNeedsReplyFilter':
                scenarioTypeCondition = shouldUsePrompt
                    ? !!row.HasTask && needsReplyItemIds.includes(row.ItemId?.Id)
                    : !!row.CopilotNeedsReply;
                break;
            case 'catchUpNeedsActionFilter':
                scenarioTypeCondition = shouldUsePrompt
                    ? needsActionItemIds.includes(row.ItemId?.Id)
                    : !!row.HasTask;
                break;
            case 'catchUpNeedsScheduleFilter':
                scenarioTypeCondition = shouldUsePrompt
                    ? needsScheduleItemIds.includes(row.ItemId?.Id)
                    : true;
                break;
            default:
                scenarioTypeCondition = false;
        }
        const rowDateTime = new Date(row.DateTimeReceived ?? '');
        return (
            scenarioTypeCondition &&
            (catchUpIncludeAllEmail || row.IsRead === false) &&
            isInSelectedTimeFrame(rowDateTime, timeFrame ?? '3days')
        );
    });
}

export function filterConversationRowsForCatchUp(
    rows: ConversationType[],
    scenarioType: MailFolderScenarioType,
    mailboxInfo: MailboxInfo
): ConversationType[] {
    const {
        catchUpIncludeAllEmail,
        timeFrame,
        shouldUsePrompt,
        needsReplyConversationIds,
        needsActionConversationIds,
        needsScheduleConversationIds,
    } = getStore(mailboxInfo);

    return rows.filter(row => {
        let scenarioTypeCondition = false;
        switch (scenarioType) {
            case 'catchUpHighPriorityFilter':
                scenarioTypeCondition = (row.ConversationCopilotInboxScore ?? 0) >= 4;
                break;
            case 'catchUpNeedsReplyFilter':
                scenarioTypeCondition = shouldUsePrompt
                    ? needsReplyConversationIds.includes(row.ConversationId.Id)
                    : !!row.ConversationCopilotNeedsReply;
                break;
            case 'catchUpNeedsActionFilter':
                scenarioTypeCondition =
                    shouldUsePrompt && needsActionConversationIds.includes(row.ConversationId.Id);
                break;
            case 'catchUpNeedsScheduleFilter':
                scenarioTypeCondition = shouldUsePrompt
                    ? needsScheduleConversationIds.includes(row.ConversationId.Id)
                    : true;
                break;
            default:
                scenarioTypeCondition = false;
        }
        const rowDateTime = new Date(row.LastDeliveryTime ?? '');
        return (
            scenarioTypeCondition &&
            (catchUpIncludeAllEmail || row.UnreadCount > 0) &&
            isInSelectedTimeFrame(rowDateTime, timeFrame ?? '3days')
        );
    });
}

function isInSelectedTimeFrame(rowDateTime: Date, timeFrame: string): boolean {
    const currentDateTime = new Date(rowDateTime).getTime();

    switch (timeFrame) {
        case '3days':
            return currentDateTime > Date.now() - 3 * 24 * 60 * 60 * 1000;
        case '2days':
            return currentDateTime > Date.now() - 2 * 24 * 60 * 60 * 1000;
        case '24hours':
            return currentDateTime > Date.now() - 24 * 60 * 60 * 1000;
        default:
            return false;
    }
}
